import type { Point, Rect } from '@geom';
import { Elements, type Edges, type EID, type NID, type Nodes } from '@graph';
import { LayoutConstants, SpatialIndex, SpatialIndexItemType } from '@layout';
import type { Doc } from './Doc';

/**
 * Returns the smallest enclosing group node under the pointer,
 * or `undefined` if none.
 */
export function findGroupAt(
  doc: Doc,
  docPoint: Point,
  spatialIndex: SpatialIndex | undefined
): NID | undefined {
  if (!spatialIndex) return undefined;

  const candidateElements = findCandidateElements(docPoint, spatialIndex);

  let smallest: NID | undefined;
  let smallestArea = Infinity;
  for (const node of candidateElements.nodes) {
    if (!doc.graph.hasNode(node)) {
      console.warn(`findGroupAt: node ${node} in spatial index, but not in graph`);
      continue;
    }
    if (doc.isGroup(node)) {
      const area = doc.nodeBounds(node).area;
      if (area < smallestArea) {
        smallest = node;
        smallestArea = area;
      }
    }
  }
  return smallest;
}

/**
 * Returns *all* elements whose spatial-index bounds intersect
 * the given point or rectangle.
 */
function findCandidateElements(
  docArea: Point | Rect,
  spatialIndex: SpatialIndex
): Elements {
  const nodes: Nodes = new Set();
  const edges: Edges = new Set();

  const items = spatialIndex.query(docArea);
  for (const item of items) {
    if (item.type === SpatialIndexItemType.NODE) {
      nodes.add(item.id);
    } else {
      edges.add(item.id);
    }
  }
  return Elements.make(nodes, edges);
}

/**
 * Returns the set of elements whose true geometry intersects `docArea`.
 * Uses candidate filtering via the spatial index followed by precise
 * geometry checks.
 */
export function findElementsIntersectingRect(
  doc: Doc,
  docArea: Rect,
  spatialIndex: SpatialIndex
): Elements {
  const nodes: Nodes = new Set();
  const edges: Edges = new Set();

  const candidates = findCandidateElements(docArea, spatialIndex);

  for (const node of candidates.nodes) {
    if (!doc.graph.hasNode(node)) {
      console.warn(
        `findElementsIntersectingRect: node ${node} in spatial index, but not in graph`
      );
      continue;
    }
    if (doc.nodeBounds(node).intersects(docArea)) {
      nodes.add(node);
    }
  }

  for (const edge of candidates.edges) {
    if (!doc.graph.hasEdge(edge)) {
      console.warn(
        `findElementsIntersectingRect: edge ${edge} in spatial index, but not in graph`
      );
      continue;
    }
    if (doc.edgeLayout(edge).path.intersectsRect(docArea)) {
      edges.add(edge);
    }
  }

  return Elements.make(nodes, edges);
}

/**
 * Hit-test helper used by the UI for hover behaviour.
 * Prioritises leaf nodes, then edges, then groups.
 */
export function findHoveredElementAt(
  doc: Doc,
  docPoint: Point,
  spatialIndex: SpatialIndex
): Elements {
  const nodes: Nodes = new Set();
  const edges: Edges = new Set();

  const candidates = findCandidateElements(docPoint, spatialIndex);

  /* 1 ─ leaf nodes */
  for (const node of candidates.nodes) {
    if (!doc.graph.hasNode(node)) {
      console.warn(
        `findHoveredElementAt: node ${node} in spatial index, but not in graph`
      );
      continue;
    }
    if (!doc.isGroup(node)) {
      nodes.add(node);
      return Elements.make(nodes, edges);
    }
  }

  /* 2 ─ closest edge within max distance */
  let closest: EID | undefined;
  let closestDist = Infinity;
  for (const edge of candidates.edges) {
    if (!doc.graph.hasEdge(edge)) {
      console.warn(
        `findHoveredElementAt: edge ${edge} in spatial index, but not in graph`
      );
      continue;
    }
    const { d } = doc.edgeLayout(edge).path.project(docPoint);
    if (d < closestDist) {
      closest = edge;
      closestDist = d;
    }
  }
  if (closest !== undefined && closestDist < LayoutConstants.HOVERED_EDGE_MAX_DISTANCE) {
    edges.add(closest);
    return Elements.make(nodes, edges);
  }

  /* 3 ─ smallest enclosing group */
  let smallest: NID | undefined;
  let smallestArea = Infinity;
  for (const node of candidates.nodes) {
    if (!doc.graph.hasNode(node)) {
      console.warn(
        `findHoveredElementAt: node ${node} in spatial index, but not in graph`
      );
      continue;
    }
    if (doc.isGroup(node)) {
      const area = doc.nodeBounds(node).area;
      if (area < smallestArea) {
        smallest = node;
        smallestArea = area;
      }
    }
  }
  if (smallest) nodes.add(smallest);

  return Elements.make(nodes, edges);
}

/**
 * Variant of `findHoveredElementAt` that returns only the group ID (if any).
 */
export function findHoveredGroupAt(
  doc: Doc,
  docPoint: Point,
  spatialIndex: SpatialIndex
): NID | undefined {
  const candidates = findCandidateElements(docPoint, spatialIndex);

  let smallest: NID | undefined;
  let smallestArea = Infinity;
  for (const node of candidates.nodes) {
    if (!doc.graph.hasNode(node)) {
      console.warn(`findHoveredGroupAt: node ${node} in spatial index, but not in graph`);
      continue;
    }
    if (doc.isGroup(node)) {
      const area = doc.nodeBounds(node).area;
      if (area < smallestArea) {
        smallest = node;
        smallestArea = area;
      }
    }
  }
  return smallest;
}
